import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Vite-served static assets resolve to URLs like `/assets/foo-hash.jpeg`
// in prod builds and `/@fs/...` or `/src/...` in dev. These must pass
// through untouched — they are not object-storage keys.
function isStaticAssetPath(path: string): boolean {
  return (
    path.startsWith("/assets/") ||
    path.startsWith("/@") ||
    path.startsWith("/src/") ||
    path.startsWith("blob:")
  );
}

export function getImageUrl(path?: string | null) {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  if (isStaticAssetPath(path)) return path;
  // Idempotent: an already-resolved `/api/storage/objects/...` path must
  // pass through unchanged so callers that don't know the source format
  // can wrap unconditionally without double-prefixing.
  if (path.startsWith("/api/storage/objects/") || path.startsWith("/api/storage/")) return path;
  const cleanPath = path.startsWith("/objects/") ? path.slice("/objects/".length) : path.replace(/^\//, "");
  return `/api/storage/objects/${cleanPath}`;
}

export function getThumbnailUrl(path?: string | null, width = 300) {
  if (!path) return "";
  if (path.startsWith("data:")) return path;
  if (path.startsWith("http")) return path;
  if (isStaticAssetPath(path)) return path;
  // Idempotent for already-resolved storage paths — append width/version
  // params if a `w=` isn't already present so we still get the resized
  // variant even when the URL already carries unrelated query string.
  if (path.startsWith("/api/storage/objects/") || path.startsWith("/api/storage/")) {
    if (/[?&]w=/.test(path)) return path;
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}w=${width}&v=2`;
  }
  const cleanPath = path.startsWith("/objects/") ? path.slice("/objects/".length) : path.replace(/^\//, "");
  return `/api/storage/objects/${cleanPath}?w=${width}&v=2`;
}

/**
 * Build a `srcset` value targeting both 1x and 2x DPRs for grid thumbnails.
 * Returns the comma-joined string; pair with a `sizes` attribute on the
 * `<img>` so the browser picks the smallest variant that fits the slot.
 * Pass-through (returns empty) for non-storage URLs that can't be width-
 * resized (data:, http, static assets, blobs).
 */
export function getThumbnailSrcSet(
  path: string | undefined | null,
  widths: readonly number[],
): string {
  if (!path) return "";
  if (path.startsWith("data:") || path.startsWith("http") || path.startsWith("blob:")) return "";
  if (isStaticAssetPath(path)) return "";
  return widths
    .map((w) => `${getThumbnailUrl(path, w)} ${w}w`)
    .join(", ");
}

export function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Render a credit amount as a compact string, trimming trailing zeros so
 * fractional values from `usage_events.creditsConsumed` (numeric(10,2))
 * read as e.g. "1", "1.5", "2.5" instead of "1.00" or long floats.
 */
export function formatCredits(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, "");
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
